import React, { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  CheckCircleOutline,
  DescriptionOutlined,
  EditOutlined,
  ExpandLess,
  ExpandMore,
  FactCheckOutlined,
  GavelOutlined,
  HistoryOutlined,
  InfoOutlined,
  MedicalInformationOutlined,
  PersonOutline,
  SaveOutlined,
  TimelineOutlined,
  VisibilityOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import type { SvgIconComponent } from '@mui/icons-material';
import {
  BrighterGreen,
  DarkGreen,
  PrimaryTitleGray,
  ShadowColor,
} from '../../../../app/theme/tokens/colors';
import {
  PatientDetailsForm,
  EditableSection,
  type PatientDocumentReplacementFile,
  type PatientDocumentUploadFile,
} from './PatientDetailsForm';
import { listCompanies } from '../../../../services/configuration/companiesService';
import {
  getPatientV2DocumentContent,
  getPatientV2SapCompanies,
  type PatientV2CompanyOption,
} from '../../../../services/patients/patientsV2Service';
import { usePortalStore } from '../../../../stores/portalStore';
import { PatientReviewActions } from './PatientReviewActions';
import { PatientDocumentReviewActions } from './PatientDocumentReviewActions';
import { PatientTriageTimeline } from './PatientTriageTimeline';
import { PatientRegulatoryDecision } from './PatientRegulatoryDecision';
import { PatientEquipmentSelection } from './PatientEquipmentSelection';
import type {
  PatientV2RegulatoryDecisionPayload,
  PatientV2ReviewPayload,
  PatientV2ReviewSectionPayload,
  PatientV2TriageItemResponse,
  PatientV2TriageNotePayload,
} from '../../../../services/patients/patientsV2Service';
import type {
  PatientDetailsMode,
  PatientDetailsPatch,
  PatientV2Detail,
  PatientV2Document,
  PatientV2HistoryItem,
  PatientV2ReviewIssue,
  ReviewSectionKey,
  ReviewSectionStatus,
} from './types';
import { canMakeRegulatoryDecisionV2, canReviewPatientsV2 } from './types';
import { formatCNPJ, formatCPF, formatDate } from '../../../../utils/format';
import { Company } from '../../../../types/companies/Companies';

const DETAILS_FORM_ID = 'patients-v2-details-form';

const modeCopy: Record<
  PatientDetailsMode,
  { title: string; subtitle: string; action: string }
> = {
  view: {
    title: 'Cadastro e acompanhamento do paciente',
    subtitle: 'Visualize as informações do paciente e da solicitação.',
    action: 'Editar dados',
  },
  create: {
    title: 'Cadastro de paciente',
    subtitle: 'Preencha e acompanhe as informações da solicitação do paciente.',
    action: 'Salvar cadastro',
  },
  review: {
    title: 'Análise de solicitação do paciente',
    subtitle: 'Avalie as informações e a documentação da solicitação.',
    action: 'Salvar avaliação',
  },
};

const statusColors: Record<string, { color: string; background: string }> = {
  success: { color: '#287a20', background: '#eaf6e6' },
  warning: { color: '#9a5b00', background: '#fff4dc' },
  info: { color: '#1769aa', background: '#eaf4fb' },
  error: { color: '#b42318', background: '#fcebe8' },
  neutral: { color: '#46554a', background: '#eff2f0' },
};

const statusToneByLabel: Record<string, string> = {
  Aprovado: 'success',
  Ativo: 'success',
  Solicitado: 'info',
  'Em análise': 'info',
  Pendente: 'warning',
  'Deferido provisório': 'success',
  'Indeferimento provisório': 'warning',
  Indeferido: 'error',
  'Pendência crítica': 'warning',
  'Reavaliação pendente': 'warning',
  'Não aprovado': 'error',
  Inativo: 'error',
};

interface FieldProps {
  label: string;
  value?: ReactNode;
  wide?: boolean;
  pending?: boolean;
}

const PendingTag = () => (
  <Chip
    label="Pendente"
    size="small"
    sx={{
      height: 20,
      color: statusColors.warning.color,
      backgroundColor: statusColors.warning.background,
      fontSize: 11,
      fontWeight: 700,
    }}
  />
);

const Field = ({ label, value, wide, pending = false }: FieldProps) => (
  <Box
    sx={{
      minWidth: 0,
      gridColumn: wide ? { xs: 'auto', md: 'span 2' } : 'auto',
    }}
  >
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      flexWrap="wrap"
      sx={{ mb: 0.25 }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {pending && <PendingTag />}
    </Stack>
    <Typography
      variant="body2"
      sx={{
        color: PrimaryTitleGray,
        fontWeight: 600,
        overflowWrap: 'anywhere',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

interface SectionCardProps {
  number: string;
  title: string;
  icon: SvgIconComponent;
  children: ReactNode;
  headerAction?: ReactNode;
  reviewMode?: boolean;
  sectionStatus?: ReviewSectionStatus;
  onConfirm?: () => void;
  hideTopBorder?: boolean;
}

const SectionCard = ({
  number,
  title,
  icon: Icon,
  children,
  headerAction,
  reviewMode = false,
  sectionStatus,
  onConfirm,
  hideTopBorder = false,
}: SectionCardProps) => (
  <Box
    sx={{
      py: { xs: 2, md: 2.5 },
      borderTop: hideTopBorder ? 0 : '1px solid #bdcbbf',
      '&:first-of-type': { borderTop: 0, pt: 0 },
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={1.5}
      sx={{ mb: 2 }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            color: BrighterGreen,
            border: `1px solid ${BrighterGreen}`,
            backgroundColor: 'transparent',
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ color: DarkGreen, fontWeight: 700 }}
        >
          {number}. {title}
        </Typography>
      </Stack>
      {headerAction ??
        (reviewMode &&
          (sectionStatus === 'confirmed' ? (
            <Chip
              icon={<CheckCircleOutline />}
              label="Seção conferida"
              size="small"
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                color: statusColors.success.color,
                backgroundColor: statusColors.success.background,
                fontWeight: 700,
              }}
            />
          ) : (
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckCircleOutline />}
              onClick={onConfirm}
              sx={{
                alignSelf: { xs: 'flex-start', sm: 'auto' },
                color: DarkGreen,
                borderColor: '#9eb7a2',
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Confirmar seção
            </Button>
          )))}
    </Stack>
    {children}
  </Box>
);

interface FieldGridProps {
  children: ReactNode;
  columns?: number;
}

const FieldGrid = ({ children, columns = 4 }: FieldGridProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: '1fr',
        sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
        md: `repeat(${columns}, minmax(0, 1fr))`,
      },
      gap: { xs: 1.5, md: 2 },
    }}
  >
    {children}
  </Box>
);

const StatusChip = ({ label, tone }: { label: string; tone?: string }) => {
  const colors =
    statusColors[tone ?? statusToneByLabel[label] ?? 'neutral'] ??
    statusColors.neutral;
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        color: colors.color,
        backgroundColor: colors.background,
        fontWeight: 700,
      }}
    />
  );
};

const DevelopmentState = ({ minHeight = 92 }: { minHeight?: number }) => (
  <Box
    sx={{
      minHeight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '1px dashed #c9d3cc',
      borderRadius: 1.5,
      backgroundColor: '#fafcfb',
    }}
  >
    <Stack alignItems="center" spacing={0.5}>
      <InfoOutlined sx={{ color: '#87968b' }} />
      <Typography variant="body2" color="text.secondary">
        Em desenvolvimento
      </Typography>
    </Stack>
  </Box>
);

interface DocumentCardProps {
  document: PatientV2Document;
  onOpenDocument: (document: PatientV2Document) => void;
  pending?: boolean;
}

const DocumentCard = ({
  document,
  onOpenDocument,
  pending = false,
}: DocumentCardProps) => {
  const colors = statusColors[document.tone] ?? statusColors.neutral;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.25,
        border: '1px solid #e5eae6',
        borderRadius: 1.5,
        minHeight: 82,
      }}
    >
      <DescriptionOutlined
        sx={{ color: colors.color, mt: 0.25 }}
        fontSize="small"
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }}>
          {document.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {document.type} · {document.size}
        </Typography>
        {document.documentGeneratedAt && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Gerado em {formatHistoryDate(document.documentGeneratedAt)}
          </Typography>
        )}
        {document.documentExpiresAt && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Válido até {formatHistoryDate(document.documentExpiresAt)}
          </Typography>
        )}
        {document.uploadedAt && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            Enviado em {formatHistoryDate(document.uploadedAt)}
          </Typography>
        )}
        {document.reviewReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25 }}
          >
            {document.reviewReason}
          </Typography>
        )}
        <Box sx={{ mt: 0.75 }}>
          {pending ? (
            <PendingTag />
          ) : (
            <StatusChip label={document.status} tone={document.tone} />
          )}
        </Box>
      </Box>
      <IconButton
        size="small"
        aria-label={`Visualizar ${document.name}`}
        onClick={() => onOpenDocument(document)}
      >
        <VisibilityOutlined fontSize="small" />
      </IconButton>
    </Box>
  );
};

const SidebarField = ({
  label,
  value,
}: {
  label: string;
  value?: ReactNode;
}) => (
  <Box>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block' }}
    >
      {label}
    </Typography>
    {React.isValidElement(value) ? (
      value
    ) : (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value || '—'}
      </Typography>
    )}
  </Box>
);

const RequestSummary = ({ patient }: { patient: PatientV2Detail }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid #e3e8e4',
      boxShadow: `0 2px 10px ${ShadowColor}`,
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{ color: DarkGreen, fontWeight: 700, mb: 1.5 }}
    >
      Resumo da solicitação
    </Typography>
    <Stack spacing={1.25}>
      <SidebarField
        label="Status atual"
        value={<StatusChip label={patient.regulatoryStatus} />}
      />
      <SidebarField label="URS solicitante" value={patient.urs} />
      <SidebarField label="STS" value={patient.sts} />
      <SidebarField label="CRS" value={patient.crs} />
      <SidebarField
        label="Data da solicitação"
        value={formatDate(patient?.history[0]?.createdAt)}
      />
      <SidebarField label="Solicitado por" value={patient.doctor} />
    </Stack>
  </Paper>
);

const formatHistoryDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('pt-BR');
};

const RequestHistory = ({ patient }: { patient: PatientV2Detail }) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid #e3e8e4',
        boxShadow: `0 2px 10px ${ShadowColor}`,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <HistoryOutlined sx={{ color: BrighterGreen }} fontSize="small" />
        <Typography
          variant="subtitle1"
          sx={{ color: DarkGreen, fontWeight: 700 }}
        >
          Histórico da solicitação
        </Typography>
      </Stack>
      {patient.history.length === 0 ? (
        <Box
          sx={{
            minHeight: 92,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed #c9d3cc',
            borderRadius: 1.5,
            backgroundColor: '#fafcfb',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum evento registrado.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25}>
          {patient.history.map((event: PatientV2HistoryItem, index) => {
            const eventId = String(
              event.id ?? `${event.title ?? 'evento'}-${event.date ?? index}`
            );
            return (
              <Box
                key={eventId}
                sx={{
                  pl: 1.25,
                  py: 0.5,
                  borderLeft: '2px solid #9eb7a2',
                  borderRadius: 1,
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                  alignItems="flex-start"
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {event.eventType ??
                        event.title ??
                        'Evento da solicitação'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatHistoryDate(event.createdAt ?? event.date)}
                    </Typography>
                  </Stack>
                  {event.regulatoryStatus && (
                    <StatusChip label={event.regulatoryStatus} />
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
};

interface ReviewPendingDetailsProps {
  issues: PatientV2ReviewIssue[];
  open: boolean;
  onToggle: () => void;
}

const ReviewPendingDetails = ({
  issues,
  open,
  onToggle,
}: ReviewPendingDetailsProps) => (
  <Box
    sx={{
      mb: 2,
      border: '1px solid #f1d69b',
      borderRadius: 2,
      backgroundColor: '#fff9e9',
      overflow: 'hidden',
    }}
  >
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={1}
      sx={{ p: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <WarningAmberOutlined sx={{ color: '#9a5b00' }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#795000' }}>
          Pendências identificadas ({issues.length})
        </Typography>
      </Stack>
      <Button
        size="small"
        endIcon={open ? <ExpandLess /> : <ExpandMore />}
        onClick={onToggle}
        sx={{
          alignSelf: { xs: 'flex-start', sm: 'auto' },
          color: '#795000',
          textTransform: 'none',
          fontWeight: 700,
        }}
      >
        {open ? 'Ocultar detalhes' : 'Ver detalhes das pendências'}
      </Button>
    </Stack>
    <Collapse in={open} timeout="auto" unmountOnExit>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        <Stack spacing={1} sx={{ borderTop: '1px solid #f1d69b', pt: 1.25 }}>
          {issues.map((issue) => (
            <Box
              key={`${issue.section}-${issue.field}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'minmax(160px, 0.8fr) minmax(180px, 1fr) 2fr',
                },
                gap: { xs: 0.25, sm: 1.5 },
                alignItems: 'baseline',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#795000', fontWeight: 700 }}
              >
                {issue.section}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#5f4b29', fontWeight: 600 }}
              >
                {issue.label || issue.field}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {issue.description}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    </Collapse>
  </Box>
);

const normalizeIssueValue = (value: unknown) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const sectionIssueAliases: Record<
  'patient' | 'clinical' | 'documents',
  string[]
> = {
  patient: ['dados do paciente', 'patient'],
  clinical: ['informacoes clinicas', 'informacoes clinica', 'clinical'],
  documents: ['documentos', 'documents'],
};

const fieldIssueAliases: Record<string, string[]> = {
  patient: ['patient', 'patient_social_reason', 'nome'],
  cellphone: ['cellphone', 'contact_number', 'telefone 1', 'celular'],
  phoneNumber: ['phoneNumber', 'contact_number_2', 'telefone 2', 'telefone'],
  address: ['address', 'delivery_address', 'endereco'],
  number: ['number', 'delivery_number', 'numero'],
  addressLine2: ['addressLine2', 'delivery_complement', 'complemento'],
  neighborhood: ['neighborhood', 'delivery_neighborhood', 'bairro'],
  city: ['city', 'delivery_city', 'cidade'],
  uf: ['uf', 'delivery_fu'],
  cep: ['cep', 'delivery_zipcode'],
  companyName: ['companyName', 'company_social_name', 'empresa'],
};

const equipmentCategories = [
  {
    id: 'oxygenotherapy',
    title: 'Oxigenoterapia',
    description: 'Selecione equipamentos para oxigenoterapia.',
    image: '/categoria/implantacao/oxigenoterapia/main.jpg',
  },
  {
    id: 'ventilation',
    title: 'Ventilação Domiciliar',
    description: 'Selecione equipamentos para ventilação.',
    image: '/categoria/implantacao/ventilacaoDomiciliar/main.jpg',
  },
  {
    id: 'sleep',
    title: 'Linha do Sono',
    description: 'Selecione equipamentos da linha do sono.',
    image: '/categoria/implantacao/linhaDoSono/main.jpg',
  },
  {
    id: 'oximeters',
    title: 'Oxímetros',
    description: 'Selecione equipamentos de monitoramento.',
    image: '/categoria/implantacao/oximetros/main.jpg',
  },
  {
    id: 'aspirators',
    title: 'Aspiradores de Secreção',
    description: 'Selecione aspiradores e acessórios.',
    image: '/categoria/implantacao/aspiradorDeSecrecao/main.jpg',
  },
  {
    id: 'accessories',
    title: 'Dispositivos e Acessórios',
    description: 'Selecione dispositivos complementares.',
    image: '/categoria/implantacao/dispositivosAcessorios/main.jpg',
  },
];

interface PatientDetailsLayoutProps {
  mode?: PatientDetailsMode;
  patient: PatientV2Detail | null;
  onBack: () => void;
  readOnly?: boolean;
  onCreate?: (
    patient: PatientV2Detail,
    documents: PatientDocumentUploadFile[]
  ) => Promise<void>;
  onResubmit?: (
    patient: PatientV2Detail,
    documentReplacements: PatientDocumentReplacementFile[]
  ) => Promise<void>;
  onReview?: (payload: PatientV2ReviewPayload) => Promise<void>;
  onSaveRegulatoryDecision?: (
    payload: PatientV2RegulatoryDecisionPayload
  ) => Promise<void>;
  triageItems?: PatientV2TriageItemResponse[];
  triageLoading?: boolean;
  triageError?: string;
  onCreateTriageNote?: (payload: PatientV2TriageNotePayload) => Promise<void>;
}

interface EquipmentRowProps {
  id: number;
  name: string;
  category: string;
  quantity: number;
  selected: boolean;
  onSelect: (id: number) => void;
  onIncrease: (id: number) => void;
  onDecrease: (id: number) => void;
}

const EquipmentRow = ({
  id,
  name,
  category,
  quantity,
  selected,
  onSelect,
  onIncrease,
  onDecrease,
}: EquipmentRowProps) => (
  <TableRow
    onClick={() => onSelect(id)}
    sx={{
      cursor: 'pointer',
      backgroundColor: selected ? '#2e8112' : 'transparent',

      '& td': {
        color: selected ? '#ffffff' : 'inherit',
        fontWeight: selected ? 600 : 400,
      },

      '&:hover': {
        backgroundColor: selected ? '#2e8112' : '#f1f8f1',
      },
    }}
  >
    <TableCell>{name}</TableCell>

    <TableCell>{category}</TableCell>

    <TableCell>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button
          variant="contained"
          color="error"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(id);
            onDecrease(id);
          }}
        >
          -
        </Button>

        <Typography>{quantity}</Typography>

        <Button
          variant="contained"
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            onSelect(id);
            onIncrease(id);
          }}
        >
          +
        </Button>
      </Stack>
    </TableCell>
  </TableRow>
);

export const PatientDetailsLayout = ({
  mode = 'view',
  patient,
  onBack,
  readOnly = false,
  onCreate,
  onResubmit,
  onReview,
  onSaveRegulatoryDecision,
  triageItems = [],
  triageLoading = false,
  triageError = '',
  onCreateTriageNote,
}: PatientDetailsLayoutProps) => {
  const currentUser = usePortalStore((state: any) => state.currentUser);
  const [feedback, setFeedback] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [draftPatient, setDraftPatient] = useState<PatientV2Detail | null>(
    patient
  );
  const [editing, setEditing] = useState(mode === 'create');
  const [pendingDetailsOpen, setPendingDetailsOpen] = useState(false);
  const [reviewSectionStatus, setReviewSectionStatus] = useState<
    Partial<Record<ReviewSectionKey, ReviewSectionStatus>>
  >({});
  const [baseCompanyOptions, setBaseCompanyOptions] = useState<
    PatientV2CompanyOption[]
  >([]);
  const [companyOptions, setCompanyOptions] = useState<
    PatientV2CompanyOption[]
  >([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [sapLoading, setSapLoading] = useState(false);
  const [sapError, setSapError] = useState('');
  const [sapLookupValue, setSapLookupValue] = useState('');
  const [evaluationStarted, setEvaluationStarted] = useState(false);
  const [evaluationCompletedLocally, setEvaluationCompletedLocally] =
    useState(false);
  const [cancelEvaluationOpen, setCancelEvaluationOpen] = useState(false);
  const [confirmEvaluationOpen, setConfirmEvaluationOpen] = useState(false);
  const [evaluationSubmitting, setEvaluationSubmitting] = useState(false);
  const [draftEvaluation, setDraftEvaluation] = useState<
    Partial<
      Record<
        'patient' | 'clinical' | 'documents',
        PatientV2ReviewSectionPayload
      >
    >
  >({});
  const [selectedEquipmentCategory, setSelectedEquipmentCategory] = useState<
    string | null
  >(null);
  const [selectedEquipment, setSelectedEquipment] = useState<number | null>(
    null
  );

  const [equipments, setEquipments] = useState([
    {
      id: 1,
      name: 'Concentrador de Oxigênio',
      category: 'Oxigenoterapia',
      quantity: 0,
    },
    {
      id: 2,
      name: 'Cilindro de Oxigênio',
      category: 'Oxigenoterapia',
      quantity: 0,
    },
    {
      id: 3,
      name: 'Concentrador Portátil',
      category: 'Oxigenoterapia',
      quantity: 0,
    },
  ]);

  const sapLookupRequest = useRef(0);
  const evaluationSubmissionInFlight = useRef(false);
  const copy = modeCopy[mode] ?? modeCopy.view;
  const isReview = mode === 'review';
  const approvalReview = isReview && Boolean(onReview);
  const isCreate = mode === 'create';
  const showSap = Boolean(currentUser?.isAdmin || currentUser?.isWhiteMartins);
  const canReview = canReviewPatientsV2(currentUser?.patientV2Profile);
  const canMakeRegulatoryDecision = canMakeRegulatoryDecisionV2(
    currentUser?.patientV2Profile
  );
  const activePatient = draftPatient ?? ({} as PatientV2Detail);
  const equipmentCompany = companyOptions.find(
    (company) => company.id === activePatient.companyId
  );
  const equipmentPatient = {
    ...activePatient,
    company: equipmentCompany
      ? {
          id: equipmentCompany.id,
          cnpj: equipmentCompany.cnpj,
          empresa: equipmentCompany.companyName,
        }
      : activePatient.company?.id === activePatient.companyId
      ? activePatient.company
      : undefined,
  };
  const canResubmit =
    !isCreate &&
    !isReview &&
    Boolean(activePatient.canEdit) &&
    [
      'Em preenchimento',
      'Em análise',
      'Pendente',
      'Indeferimento provisório',
    ].includes(activePatient.regulatoryStatus) &&
    Boolean(onResubmit);
  const canStartEvaluation =
    isReview &&
    approvalReview &&
    Boolean(activePatient.canStartReview) &&
    !evaluationStarted &&
    !evaluationCompletedLocally;
  const needsDocumentDecision = activePatient.documents.length > 0;
  const evaluationComplete = Boolean(
    draftEvaluation.patient?.decision &&
      draftEvaluation.clinical?.decision &&
      (!needsDocumentDecision || draftEvaluation.documents?.decision)
  );

  const handleIncrease = (id: number) => {
    setEquipments((current) =>
      current.map((equipment) =>
        equipment.id === id
          ? {
              ...equipment,
              quantity: equipment.quantity + 1,
            }
          : equipment
      )
    );
  };

  const handleDecrease = (id: number) => {
    setEquipments((current) =>
      current.map((equipment) =>
        equipment.id === id
          ? {
              ...equipment,
              quantity: equipment.quantity > 0 ? equipment.quantity - 1 : 0,
            }
          : equipment
      )
    );
  };

  useEffect(() => {
    setDraftPatient(patient);
    setEditing(mode === 'create');
    setPendingDetailsOpen(false);
    setReviewSectionStatus({ ...(patient?.review?.sectionStatus ?? {}) });
    setSubmitError('');
    setEvaluationStarted(false);
    setEvaluationCompletedLocally(false);
    setDraftEvaluation({});
    setCancelEvaluationOpen(false);
    setConfirmEvaluationOpen(false);
  }, [mode, patient]);

  const showFeedback = (message: string) => setFeedback(message);
  const updateDraftPatient = (patch: PatientDetailsPatch) => {
    return setDraftPatient((current) => {
      const base = current ?? ({} as PatientV2Detail);
      return {
        ...base,
        ...patch,
        clinical: patch.clinical
          ? { ...(base.clinical ?? {}), ...patch.clinical }
          : base.clinical,
      } as PatientV2Detail;
    });
  };

  useEffect(() => {
    if (!isCreate && !canResubmit) return;
    let active = true;
    setCompanyLoading(true);
    void listCompanies()
      .then((companies: any[]) => {
        if (!active) return;
        const options = (Array.isArray(companies) ? companies : [])
          .map((company: Company) => ({
            id: Number(company.id),
            cnpj: String(company?.cnpj ?? ''),
            companyName: String(company?.empresa ?? ''),
          }))
          .filter((company: PatientV2CompanyOption) => Boolean(company.cnpj));
        setBaseCompanyOptions(options);
        setCompanyOptions(options);
      })
      .catch(() => {
        if (active)
          setSubmitError('Não foi possível carregar as empresas disponíveis.');
      })
      .finally(() => {
        if (active) setCompanyLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canResubmit, isCreate]);

  useEffect(() => {
    if (!isCreate || !showSap) return;
    const sap = sapLookupValue.trim();
    if (!sap) {
      setSapError('');
      setCompanyOptions(baseCompanyOptions);
      return;
    }

    const requestId = ++sapLookupRequest.current;
    const timeout = window.setTimeout(() => {
      setSapLoading(true);
      setSapError('');
      void getPatientV2SapCompanies(sap)
        .then((response) => {
          if (requestId !== sapLookupRequest.current) return;
          const options = Array.isArray(response.companies)
            ? response.companies
            : [];
          if (options.length === 0) {
            setCompanyOptions([]);
            updateDraftPatient({ companyId: 0 });
            setSapError(
              'O código SAP não possui CNPJ vinculado permitido para este usuário.'
            );
            return;
          }

          setCompanyOptions(options);
          const selectedCompany = options.length === 1 ? options[0] : null;
          updateDraftPatient({
            ...response.patient,
            sap,
            cnpj: selectedCompany?.cnpj ?? '',
            companyName: selectedCompany?.companyName ?? '',
          } as PatientDetailsPatch);
        })
        .catch((error: any) => {
          if (requestId !== sapLookupRequest.current) return;
          setCompanyOptions([]);
          updateDraftPatient({ companyId: 0 });
          setSapError(
            error?.message ??
              'Não foi possível validar o vínculo do código SAP.'
          );
        })
        .finally(() => {
          if (requestId === sapLookupRequest.current) setSapLoading(false);
        });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, [baseCompanyOptions, isCreate, sapLookupValue, showSap]);
  const confirmReviewSection = (section: ReviewSectionKey) => {
    setReviewSectionStatus((current) => ({
      ...current,
      [section]: 'confirmed',
    }));
    showFeedback('Seção conferida — wireframe demonstrativo');
  };
  const openDocument = async (document: PatientV2Document) => {
    const previewWindow = window.open('', '_blank');
    try {
      const result = await getPatientV2DocumentContent(
        activePatient.id,
        document.id
      );
      const binary = window.atob(result.contentBase64);
      const bytes = Uint8Array.from(binary, (character) =>
        character.charCodeAt(0)
      );
      const url = URL.createObjectURL(
        new Blob([bytes], {
          type: result.mimeType || 'application/octet-stream',
        })
      );
      if (previewWindow) previewWindow.location.href = url;
      else window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error: any) {
      previewWindow?.close();
      showFeedback(error?.message ?? 'Não foi possível abrir o documento.');
    }
  };
  const handleFormSubmit = async (
    submittedPatient: PatientV2Detail,
    documents: File[] = [],
    documentReplacements: PatientDocumentReplacementFile[] = []
  ) => {
    setSubmitError('');
    if (isCreate && onCreate) {
      try {
        await onCreate(submittedPatient, documents);
      } catch (error: any) {
        setSubmitError(
          error?.message ?? 'Não foi possível cadastrar o paciente.'
        );
      }
      return;
    }
    if (canResubmit && onResubmit) {
      try {
        await onResubmit(submittedPatient, documentReplacements);
        setEditing(false);
        showFeedback('Solicitação atualizada e enviada para nova análise.');
      } catch (error: any) {
        setSubmitError(
          error?.message ?? 'Não foi possível atualizar a solicitação.'
        );
      }
      return;
    }
    setEditing(false);
    showFeedback(
      isReview
        ? 'Avaliação preparada — wireframe demonstrativo'
        : 'Alterações preparadas — wireframe demonstrativo'
    );
  };
  const reviewIssues = activePatient?.review?.issues ?? [];
  const hasPersistedIssue = (
    section: 'patient' | 'clinical' | 'documents',
    field: string,
    documentId?: PatientV2Document['id']
  ) => {
    const allowedSections =
      sectionIssueAliases[section].map(normalizeIssueValue);
    const allowedFields = (fieldIssueAliases[field] ?? [field]).map(
      normalizeIssueValue
    );
    return reviewIssues.some((issue) => {
      const sameSection = allowedSections.includes(
        normalizeIssueValue(issue.section)
      );
      const sameField =
        allowedFields.includes(normalizeIssueValue(issue.field)) ||
        allowedFields.includes(normalizeIssueValue(issue.label));
      const sameDocument =
        documentId === undefined ||
        String(issue.documentId ?? '') === String(documentId);
      return (
        sameSection && (section === 'documents' ? sameDocument : sameField)
      );
    });
  };
  const hasDraftIssue = (section: 'patient' | 'clinical', field: string) => {
    if (!isReview || draftEvaluation[section]?.decision !== 'pending')
      return false;
    const allowedFields = (fieldIssueAliases[field] ?? [field]).map(
      normalizeIssueValue
    );
    return (draftEvaluation[section]?.pendencies ?? []).some((pendency) => {
      return (
        allowedFields.includes(normalizeIssueValue(pendency.field)) ||
        allowedFields.includes(normalizeIssueValue(pendency.label))
      );
    });
  };
  const fieldHasPending = (section: 'patient' | 'clinical', field: string) =>
    hasPersistedIssue(section, field) || hasDraftIssue(section, field);
  const anyFieldHasPending = (
    section: 'patient' | 'clinical',
    fields: string[]
  ) => fields.some((field) => fieldHasPending(section, field));
  const documentHasPending = (document: PatientV2Document) =>
    document.status === 'Pendente' ||
    hasPersistedIssue('documents', '', document.id) ||
    (isReview &&
      draftEvaluation.documents?.decision === 'pending' &&
      (draftEvaluation.documents.documentPendencies ?? []).some(
        (item) => String(item.documentId) === String(document.id)
      ));
  const updateEvaluationSection =
    (section: 'patient' | 'clinical' | 'documents') =>
    (value?: PatientV2ReviewSectionPayload) => {
      setDraftEvaluation((current) => {
        const next = { ...current };
        if (value) next[section] = value;
        else delete next[section];
        return next;
      });
    };
  const cancelEvaluation = () => {
    setDraftEvaluation({});
    setEvaluationStarted(false);
    setCancelEvaluationOpen(false);
  };
  const confirmEvaluation = async () => {
    if (
      !onReview ||
      !draftEvaluation.patient ||
      !draftEvaluation.clinical ||
      (needsDocumentDecision && !draftEvaluation.documents)
    )
      return;
    if (evaluationSubmissionInFlight.current) return;

    evaluationSubmissionInFlight.current = true;
    setEvaluationSubmitting(true);
    setSubmitError('');
    try {
      await onReview({
        sections: {
          patient: draftEvaluation.patient,
          clinical: draftEvaluation.clinical,
          ...(needsDocumentDecision
            ? { documents: draftEvaluation.documents }
            : {}),
        },
      });
      setConfirmEvaluationOpen(false);
      setEvaluationStarted(false);
      setEvaluationCompletedLocally(true);
      setDraftEvaluation({});
    } catch (error: any) {
      setSubmitError(
        error?.message ?? 'Não foi possível confirmar a avaliação.'
      );
      setConfirmEvaluationOpen(false);
    } finally {
      evaluationSubmissionInFlight.current = false;
      setEvaluationSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100%',
        p: { xs: 2, md: 3 },
        boxSizing: 'border-box',
        backgroundColor: '#ececec',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'flex-start' }}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.75 }}
            >
              <Button
                size="small"
                startIcon={<ArrowBack />}
                onClick={onBack}
                sx={{
                  color: DarkGreen,
                  minWidth: 0,
                  px: 0,
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                Voltar
              </Button>
              <StatusChip
                label={activePatient?.regulatoryStatus || 'Em análise'}
              />
            </Stack>
            <Typography
              variant="h4"
              sx={{ color: PrimaryTitleGray, fontWeight: 700, mb: 0.5 }}
            >
              {copy.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {copy.subtitle}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                Protocolo
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {activePatient?.protocol || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Data da solicitação:{' '}
                {new Intl.DateTimeFormat('pt-BR').format(
                  new Date(activePatient?.history[0]?.createdAt ?? new Date())
                ) || '—'}
              </Typography>
            </Box>
            {canStartEvaluation && (
              <Button
                variant="contained"
                startIcon={<FactCheckOutlined />}
                onClick={() => setEvaluationStarted(true)}
                sx={{
                  backgroundColor: BrighterGreen,
                  '&:hover': { backgroundColor: DarkGreen },
                  whiteSpace: 'nowrap',
                }}
              >
                Iniciar avaliação
              </Button>
            )}
            {approvalReview && evaluationStarted && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={() => setCancelEvaluationOpen(true)}
                  sx={{ textTransform: 'none' }}
                >
                  Cancelar avaliação
                </Button>
                <Button
                  variant="contained"
                  disabled={!evaluationComplete || evaluationSubmitting}
                  onClick={() => setConfirmEvaluationOpen(true)}
                  sx={{
                    backgroundColor: BrighterGreen,
                    '&:hover': { backgroundColor: DarkGreen },
                    textTransform: 'none',
                  }}
                >
                  Confirmar avaliação
                </Button>
              </Stack>
            )}
            {!readOnly &&
              (editing ? (
                <Button
                  type="submit"
                  form={DETAILS_FORM_ID}
                  variant="contained"
                  startIcon={<SaveOutlined />}
                  sx={{
                    backgroundColor: BrighterGreen,
                    '&:hover': { backgroundColor: DarkGreen },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mode === 'create'
                    ? 'Cadastrar paciente'
                    : isReview
                    ? 'Salvar avaliação'
                    : 'Salvar alterações'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<EditOutlined />}
                  onClick={(event) => {
                    event.preventDefault();
                    setEditing(true);
                  }}
                  sx={{
                    backgroundColor: BrighterGreen,
                    '&:hover': { backgroundColor: DarkGreen },
                    whiteSpace: 'nowrap',
                  }}
                >
                  {copy.action}
                </Button>
              ))}
          </Stack>
        </Stack>

        {!isCreate && !isReview && reviewIssues.length > 0 && (
          <ReviewPendingDetails
            issues={reviewIssues}
            open={pendingDetailsOpen}
            onToggle={() => setPendingDetailsOpen((current) => !current)}
          />
        )}
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}
        {approvalReview && evaluationStarted && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: '1px solid #dce7de',
              borderRadius: 2,
              backgroundColor: '#f8fbf8',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ color: DarkGreen, fontWeight: 700, mb: 1 }}
            >
              Resumo da avaliação em andamento
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
            >
              {[
                ['Dados do paciente', draftEvaluation.patient?.decision],
                ['Informações clínicas', draftEvaluation.clinical?.decision],
                ...(needsDocumentDecision
                  ? [['Documentos', draftEvaluation.documents?.decision]]
                  : [['Documentos', 'not_applicable']]),
              ].map(([label, decision]) => {
                const text =
                  decision === 'approved'
                    ? 'Aprovado'
                    : decision === 'pending'
                    ? 'Pendente'
                    : decision === 'not_applicable'
                    ? 'Não aplicável'
                    : 'Aguardando avaliação';
                const tone =
                  decision === 'approved'
                    ? 'success'
                    : decision === 'pending'
                    ? 'warning'
                    : 'info';
                return (
                  <Stack
                    key={String(label)}
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                  >
                    <Typography variant="body2">{label}</Typography>
                    <StatusChip label={text} tone={tone} />
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              minWidth: 0,
              px: { xs: 1.75, md: 2.5 },
              pt: { xs: 1.5, md: 2.5 },
              backgroundColor: 'white',
              borderRadius: 2,
              boxShadow: `0 2px 10px ${ShadowColor}`,
              border: '1px solid #e3e8e4',
            }}
          >
            {editing ? (
              <PatientDetailsForm
                patient={activePatient}
                formId={DETAILS_FORM_ID}
                onChange={updateDraftPatient}
                onSubmit={handleFormSubmit}
                reviewMode={isReview}
                reviewSectionStatus={reviewSectionStatus}
                onConfirmSection={confirmReviewSection}
                patientOnly={isCreate}
                showClinical
                showDocuments={isCreate || canResubmit}
                showSap={showSap}
                sapLoading={sapLoading}
                sapError={sapError}
                companyOptions={companyOptions}
                companyLoading={companyLoading}
                onSapChange={setSapLookupValue}
                onCompanyChange={(company) =>
                  updateDraftPatient({ companyId: company?.id })
                }
              />
            ) : (
              <>
                <SectionCard
                  number="1"
                  title="Dados do paciente"
                  icon={PersonOutline}
                  headerAction={
                    approvalReview && evaluationStarted ? (
                      <PatientReviewActions
                        section="patient"
                        patient={activePatient}
                        value={draftEvaluation.patient}
                        onChange={updateEvaluationSection('patient')}
                      />
                    ) : undefined
                  }
                >
                  <Stack spacing={2}>
                    <FieldGrid columns={5}>
                      <Field
                        label="Nome"
                        value={activePatient?.patient}
                        pending={fieldHasPending('patient', 'patient')}
                      />
                      <Field label="CNS" value={activePatient?.cns} />
                      <Field
                        label="SAP"
                        value={activePatient?.sap}
                        pending={fieldHasPending('patient', 'sap')}
                      />
                      <Field
                        label="CPF"
                        value={formatCPF(activePatient?.cpf)}
                        pending={fieldHasPending('patient', 'cpf')}
                      />
                      <Field
                        label="Data de nascimento"
                        value={formatDate(activePatient?.birthDate)}
                        pending={fieldHasPending('patient', 'birthDate')}
                      />
                      <Field
                        label="E-mail"
                        value={activePatient?.email}
                        pending={fieldHasPending('patient', 'email')}
                      />
                      <Field
                        label="Empresa"
                        value={activePatient?.company?.empresa}
                        pending={fieldHasPending('patient', 'companyId')}
                        wide
                      />
                      <Field
                        label="CNPJ"
                        value={formatCNPJ(String(activePatient?.company?.cnpj))}
                        pending={fieldHasPending('patient', 'companyId')}
                      />
                      <Field
                        label="Endereço"
                        value={[
                          activePatient?.address,
                          activePatient?.number
                            ? `Nº ${activePatient.number}`
                            : '',
                          activePatient?.addressLine2,
                          activePatient?.neighborhood,
                          [activePatient?.city, activePatient?.uf]
                            .filter(Boolean)
                            .join(' / '),
                          activePatient?.cep ? `CEP ${activePatient.cep}` : '',
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        pending={anyFieldHasPending('patient', [
                          'address',
                          'number',
                          'addressLine2',
                          'neighborhood',
                          'city',
                          'uf',
                          'cep',
                        ])}
                        wide
                      />
                      <Field
                        label="Telefones"
                        value={activePatient?.phones}
                        pending={anyFieldHasPending('patient', [
                          'cellphone',
                          'phoneNumber',
                        ])}
                      />
                      <Field
                        label="URS solicitante"
                        value={activePatient?.urs}
                        pending={fieldHasPending('patient', 'urs')}
                      />
                      <Field
                        label="STS"
                        value={activePatient?.sts}
                        pending={fieldHasPending('patient', 'sts')}
                      />
                      <Field
                        label="CRS"
                        value={activePatient?.crs}
                        pending={fieldHasPending('patient', 'crs')}
                      />
                    </FieldGrid>
                  </Stack>
                </SectionCard>

                <SectionCard
                  number="2"
                  title="Informações clínicas"
                  icon={MedicalInformationOutlined}
                  headerAction={
                    approvalReview && evaluationStarted ? (
                      <PatientReviewActions
                        section="clinical"
                        patient={activePatient}
                        value={draftEvaluation.clinical}
                        onChange={updateEvaluationSection('clinical')}
                      />
                    ) : undefined
                  }
                >
                  <Stack spacing={2}>
                    <FieldGrid columns={4}>
                      <Field
                        label="CID principal"
                        value={activePatient?.clinical?.cidPrimary}
                        pending={fieldHasPending('clinical', 'cidPrimary')}
                      />
                      <Field
                        label="CID secundário"
                        value={activePatient?.clinical?.cidSecondary}
                        pending={fieldHasPending('clinical', 'cidSecondary')}
                      />
                      <Field
                        label="Diagnóstico clínico"
                        value={activePatient?.clinical?.diagnosis}
                        pending={fieldHasPending(
                          'clinical',
                          'clinicalDiagnosis'
                        )}
                        wide
                      />
                      <Field
                        label="Indicação de O₂"
                        value={activePatient?.clinical?.oxygenIndication}
                        pending={fieldHasPending(
                          'clinical',
                          'oxygenIndication'
                        )}
                        wide
                      />
                    </FieldGrid>
                    <Box sx={{ pt: 2, borderTop: '1px solid #e7ece8' }}>
                      <FieldGrid columns={4}>
                        <Field
                          label="Fonte de O₂ prescrita"
                          value={activePatient?.clinical?.oxygenSource}
                          pending={fieldHasPending(
                            'clinical',
                            'prescribedOxygenSource'
                          )}
                        />
                        <Field
                          label="Fluxo (L/min)"
                          value={activePatient?.clinical?.oxygenFlow}
                          pending={fieldHasPending(
                            'clinical',
                            'oxygenFlowLMin'
                          )}
                        />
                        <Field
                          label="Horas de uso/dia"
                          value={activePatient?.clinical?.dailyUse}
                          pending={fieldHasPending('clinical', 'dailyUseHours')}
                        />
                        <Field
                          label="Equipamento solicitado"
                          value={activePatient?.clinical?.requestedEquipment}
                          pending={fieldHasPending(
                            'clinical',
                            'requestedEquipment'
                          )}
                        />
                      </FieldGrid>
                    </Box>
                    <Box sx={{ pt: 2, borderTop: '1px solid #e7ece8' }}>
                      <FieldGrid columns={4}>
                        <Field
                          label="Médico solicitante"
                          value={activePatient?.doctor}
                          pending={fieldHasPending(
                            'clinical',
                            'requestingDoctorName'
                          )}
                          wide
                        />
                        <Field
                          label="CRM do médico"
                          value={activePatient?.clinical?.requestingDoctorCrm}
                          pending={fieldHasPending(
                            'clinical',
                            'requestingDoctorCrm'
                          )}
                        />
                        <Field
                          label="Data da solicitação"
                          value={formatDate(activePatient?.requestedAt)}
                          pending={fieldHasPending('clinical', 'requestDate')}
                        />
                      </FieldGrid>
                    </Box>
                  </Stack>
                </SectionCard>

                <SectionCard
                  number="3"
                  title="Documentos"
                  icon={DescriptionOutlined}
                  headerAction={
                    approvalReview && evaluationStarted ? (
                      <PatientDocumentReviewActions
                        documents={activePatient.documents}
                        value={draftEvaluation.documents}
                        onChange={updateEvaluationSection('documents')}
                        onOpenDocument={openDocument}
                      />
                    ) : undefined
                  }
                >
                  {activePatient.documents.length === 0 ? (
                    <Box
                      sx={{
                        minHeight: 92,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px dashed #c9d3cc',
                        borderRadius: 1.5,
                        backgroundColor: '#fafcfb',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Nenhum documento enviado.
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: 'repeat(2, minmax(0, 1fr))',
                          md: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 1.25,
                      }}
                    >
                      {activePatient.documents.map((document) => (
                        <DocumentCard
                          key={document.id}
                          document={document}
                          pending={documentHasPending(document)}
                          onOpenDocument={(item) => void openDocument(item)}
                        />
                      ))}
                    </Box>
                  )}
                </SectionCard>
              </>
            )}

            {isReview ? (
              <>
                <SectionCard
                  number="4"
                  title="Regulatória"
                  icon={GavelOutlined}
                >
                  <PatientRegulatoryDecision
                    patient={activePatient}
                    approvalMode={Boolean(
                      canReview &&
                        activePatient.canMakeRegulatoryDecision &&
                        onSaveRegulatoryDecision
                    )}
                    canSave={Boolean(
                      canMakeRegulatoryDecision &&
                        activePatient.canMakeRegulatoryDecision &&
                        onSaveRegulatoryDecision
                    )}
                    onSave={onSaveRegulatoryDecision}
                  />
                </SectionCard>
                <SectionCard number="5" title="Triagem" icon={TimelineOutlined}>
                  <PatientTriageTimeline
                    items={triageItems}
                    loading={triageLoading}
                    error={triageError}
                    canAddNote={Boolean(canReview && onCreateTriageNote)}
                    onCreateNote={onCreateTriageNote}
                  />
                </SectionCard>
              </>
            ) : (
              <>
                {!isCreate && (
                  <SectionCard
                    number="4"
                    title="Regulatória"
                    icon={GavelOutlined}
                  >
                    <PatientRegulatoryDecision patient={activePatient} />
                  </SectionCard>
                )}

                <Box
                  sx={{
                    mt: { xs: 2, md: 2.5 },
                    borderTop: '1px solid #bdcbbf',
                  }}
                />
              </>
            )}
            {isCreate && (
              <EditableSection number="4" title="Seleção de equipamentos">
                <PatientEquipmentSelection patient={equipmentPatient} />
              </EditableSection>
            )}
          </Box>

          <Stack spacing={2}>
            <RequestSummary patient={activePatient} />
            <RequestHistory patient={activePatient} />
          </Stack>
        </Box>
      </Box>

      <Snackbar
        open={Boolean(feedback)}
        autoHideDuration={2800}
        onClose={() => setFeedback('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setFeedback('')}>
          {feedback}
        </Alert>
      </Snackbar>
      <Dialog
        open={cancelEvaluationOpen}
        onClose={() => !evaluationSubmitting && setCancelEvaluationOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancelar avaliação?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            As decisões e pendências registradas nesta avaliação serão
            descartadas. Nenhuma informação foi salva.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelEvaluationOpen(false)}>Voltar</Button>
          <Button color="error" variant="contained" onClick={cancelEvaluation}>
            Cancelar avaliação
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmEvaluationOpen}
        onClose={() => !evaluationSubmitting && setConfirmEvaluationOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar avaliação?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            As decisões de todas as seções serão salvas em um único registro no
            histórico.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmEvaluationOpen(false)}
            disabled={evaluationSubmitting}
          >
            Voltar
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmEvaluation()}
            disabled={evaluationSubmitting}
            sx={{
              backgroundColor: BrighterGreen,
              '&:hover': { backgroundColor: DarkGreen },
            }}
          >
            Confirmar avaliação
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
